/**
 * Storage service for managing evaluation data in localStorage
 * Handles serialization/deserialization of Date objects and provides type-safe CRUD operations
 */

import type {
  Evaluation,
  EvaluationTemplate,
  Scorecard,
} from '@/types/evaluation.types';
import type {
  IEvaluationStorage,
  ITemplateStorage,
  IScorecardStorage,
  IStorageUtilities,
} from './storage.interfaces';
import { generateId as createId } from '@/lib/id';
import { DateSerializer } from './serializationUtils';
import { createDefaultEvaluationTemplate } from '@/services/demo-data/defaultTemplate';

// Re-export interfaces for convenience
export type {
  IEvaluationStorage,
  ITemplateStorage,
  IScorecardStorage,
  IStorageUtilities,
} from './storage.interfaces';

// ============================================================================
// Constants
// ============================================================================

// localStorage keys
const STORAGE_KEYS = {
  EVALUATIONS: 'evaluations',
  TEMPLATE: 'template',
  SCORECARDS: 'scorecards',
  DATA_VERSION: 'dataVersion',
} as const;

// Current data version for migration strategy
const DATA_VERSION = 1;

// ============================================================================
// StorageService Class Implementation
// ============================================================================

/**
 * Main storage service class implementing all storage interfaces
 */
class StorageService
  implements
    IEvaluationStorage,
    ITemplateStorage,
    IScorecardStorage,
    IStorageUtilities
{
  // ==========================================================================
  // Private Helper Methods
  // ==========================================================================

  /**
   * Serializes an Evaluation for storage
   */
  private serializeEvaluation(evaluation: Evaluation): string {
    const serialized = {
      ...evaluation,
      targetCompletionDate: DateSerializer.serialize(evaluation.targetCompletionDate),
      statusChangedDate: DateSerializer.serialize(evaluation.statusChangedDate),
      createdDate: DateSerializer.serialize(evaluation.createdDate),
      modifiedDate: DateSerializer.serialize(evaluation.modifiedDate),
      participants: evaluation.participants.map(p => ({
        ...p,
        invitedDate: DateSerializer.serialize(p.invitedDate),
        invitationExpirationDate: DateSerializer.serialize(p.invitationExpirationDate),
        lastActivityDate: DateSerializer.serialize(p.lastActivityDate),
      })),
    };
    return JSON.stringify(serialized);
  }

  /**
   * Deserializes an Evaluation from storage
   */
  private deserializeEvaluation(data: unknown): Evaluation | null {
    try {
      // Type guard for data object
      if (typeof data !== 'object' || data === null) {
        return null;
      }

      const record = data as Record<string, unknown>;

      if (!record.id || !record.name) {
        console.warn('Invalid evaluation data: missing required fields');
        return null;
      }

      // Get default invitation expiration days (7 if not set)
      const invitationExpirationDays = typeof record.invitationExpirationDays === 'number'
        ? record.invitationExpirationDays
        : 7;

      // Migration: Add default status if missing
      const status = record.status || 'Organizing';
      const createdDate = DateSerializer.deserialize(record.createdDate as string | null) || new Date();
      const statusChangedDate = DateSerializer.deserialize(record.statusChangedDate as string | null) || createdDate;

      return {
        ...record,
        invitationExpirationDays,
        status,
        statusChangedDate,
        targetCompletionDate: DateSerializer.deserialize(record.targetCompletionDate as string | null),
        createdDate,
        modifiedDate: DateSerializer.deserialize(record.modifiedDate as string | null) || new Date(),
        participants: (Array.isArray(record.participants) ? record.participants : []).map((p: unknown) => {
          const participant = p as Record<string, unknown>;
          const invitedDate = DateSerializer.deserialize(participant.invitedDate as string | null) || new Date();

          // Migration: Add invitationExpirationDate if missing
          let invitationExpirationDate: Date | null = null;
          if (participant.invitationExpirationDate) {
            invitationExpirationDate = DateSerializer.deserialize(participant.invitationExpirationDate as string | null);
          } else if (participant.invitationStatus !== 'Accepted') {
            // Calculate expiration date for existing pending/declined participants
            const expirationDate = new Date(invitedDate);
            expirationDate.setDate(expirationDate.getDate() + invitationExpirationDays);
            invitationExpirationDate = expirationDate;
          }

          return {
            ...participant,
            invitedDate,
            invitationExpirationDate,
            lastActivityDate: DateSerializer.deserialize(participant.lastActivityDate as string | null),
          };
        }),
      } as Evaluation;
    } catch (error) {
      console.error('Failed to deserialize evaluation:', error);
      return null;
    }
  }

  /**
   * Serializes an EvaluationTemplate for storage
   */
  private serializeTemplate(template: EvaluationTemplate): string {
    const serialized = {
      ...template,
      modifiedDate: DateSerializer.serialize(template.modifiedDate),
    };
    return JSON.stringify(serialized);
  }

  /**
   * Deserializes an EvaluationTemplate from storage
   */
  private deserializeTemplate(data: unknown): EvaluationTemplate | null {
    try {
      if (typeof data !== 'object' || data === null) {
        return null;
      }

      const record = data as Record<string, unknown>;

      if (!record.id || !record.templateName) {
        console.warn('Invalid template data: missing required fields');
        return null;
      }

      return {
        ...record,
        modifiedDate: DateSerializer.deserialize(record.modifiedDate as string | null) || new Date(),
      } as EvaluationTemplate;
    } catch (error) {
      console.error('Failed to deserialize template:', error);
      return null;
    }
  }

  /**
   * Serializes a Scorecard for storage
   */
  private serializeScorecard(scorecard: Scorecard): string {
    const serialized = {
      ...scorecard,
      submittedDate: DateSerializer.serialize(scorecard.submittedDate),
      lastModified: DateSerializer.serialize(scorecard.lastModified),
    };
    return JSON.stringify(serialized);
  }

  /**
   * Deserializes a Scorecard from storage
   */
  private deserializeScorecard(data: unknown): Scorecard | null {
    try {
      if (typeof data !== 'object' || data === null) {
        return null;
      }

      const record = data as Record<string, unknown>;

      if (!record.id || !record.evaluationId) {
        console.warn('Invalid scorecard data: missing required fields');
        return null;
      }

      return {
        ...record,
        // Migration: Add participantId for existing scorecards (use a default value)
        participantId: typeof record.participantId === 'string' ? record.participantId : 'current-user',
        // Migration: Add isSubmitted field for existing scorecards
        isSubmitted: typeof record.isSubmitted === 'boolean' ? record.isSubmitted : false,
        submittedDate: DateSerializer.deserialize(record.submittedDate as string | null),
        lastModified: DateSerializer.deserialize(record.lastModified as string | null) || new Date(),
      } as Scorecard;
    } catch (error) {
      console.error('Failed to deserialize scorecard:', error);
      return null;
    }
  }

  /**
   * Handles localStorage quota exceeded errors
   */
  private handleQuotaError(): void {
    console.error('localStorage quota exceeded. Consider clearing old data.');
    alert(
      'Storage is full. Please delete some evaluations or scorecards to free up space.'
    );
  }

  /**
   * Safe localStorage getter with error handling
   */
  private getFromStorage<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to read from localStorage (${key}):`, error);
      return null;
    }
  }

  /**
   * Safe localStorage setter with error handling
   */
  private setToStorage(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.handleQuotaError();
      } else {
        console.error(`Failed to write to localStorage (${key}):`, error);
      }
      return false;
    }
  }

  /**
   * Gets all scorecards from storage (private helper)
   */
  private getAllScorecards(): Scorecard[] {
    const data = this.getFromStorage<unknown[]>(STORAGE_KEYS.SCORECARDS);
    if (!data || !Array.isArray(data)) return [];

    return data
      .map((d) => this.deserializeScorecard(d))
      .filter((s): s is Scorecard => s !== null);
  }

  // ==========================================================================
  // IEvaluationStorage Implementation
  // ==========================================================================

  /**
   * Gets all evaluations from storage
   */
  getEvaluations(): Evaluation[] {
    const data = this.getFromStorage<unknown[]>(STORAGE_KEYS.EVALUATIONS);
    if (!data || !Array.isArray(data)) return [];

    return data
      .map((d) => this.deserializeEvaluation(d))
      .filter((e): e is Evaluation => e !== null);
  }

  /**
   * Gets a single evaluation by ID
   */
  getEvaluation(id: string): Evaluation | null {
    const evaluations = this.getEvaluations();
    return evaluations.find((e) => e.id === id) || null;
  }

  /**
   * Saves an evaluation (create or update)
   */
  saveEvaluation(evaluation: Evaluation): void {
    const evaluations = this.getEvaluations();
    const index = evaluations.findIndex((e) => e.id === evaluation.id);

    if (index >= 0) {
      // Update existing
      evaluations[index] = evaluation;
    } else {
      // Create new
      evaluations.push(evaluation);
    }

    const serialized = evaluations.map((e) =>
      JSON.parse(this.serializeEvaluation(e))
    );
    this.setToStorage(STORAGE_KEYS.EVALUATIONS, JSON.stringify(serialized));
  }

  /**
   * Deletes an evaluation by ID
   */
  deleteEvaluation(id: string): void {
    const evaluations = this.getEvaluations();
    const filtered = evaluations.filter((e) => e.id !== id);

    const serialized = filtered.map((e) =>
      JSON.parse(this.serializeEvaluation(e))
    );
    this.setToStorage(STORAGE_KEYS.EVALUATIONS, JSON.stringify(serialized));
  }

  // ==========================================================================
  // ITemplateStorage Implementation
  // ==========================================================================

  /**
   * Gets the current evaluation template
   */
  getTemplate(): EvaluationTemplate | null {
    const data = this.getFromStorage<unknown>(STORAGE_KEYS.TEMPLATE);
    if (!data) return null;
    return this.deserializeTemplate(data);
  }

  /**
   * Saves the evaluation template
   */
  saveTemplate(template: EvaluationTemplate): void {
    const serialized = this.serializeTemplate(template);
    this.setToStorage(STORAGE_KEYS.TEMPLATE, serialized);
  }

  /**
   * Seeds a default template with sample criteria groups
   */
  seedDefaultTemplate(): void {
    const existingTemplate = this.getTemplate();
    if (existingTemplate) {
      console.log('Template already exists, skipping seed');
      return;
    }

    const template = createDefaultEvaluationTemplate(() => this.generateId());
    this.saveTemplate(template);
    console.log('Default template seeded successfully');
  }

  // ==========================================================================
  // IScorecardStorage Implementation
  // ==========================================================================

  /**
   * Gets all scorecards for a specific evaluation
   */
  getScorecards(evaluationId: string): Scorecard[] {
    const allScorecards = this.getAllScorecards();
    return allScorecards.filter((s) => s.evaluationId === evaluationId);
  }

  /**
   * Gets a single scorecard by ID
   */
  getScorecard(scorecardId: string): Scorecard | null {
    const scorecards = this.getAllScorecards();
    return scorecards.find((s) => s.id === scorecardId) || null;
  }

  /**
   * Saves a scorecard (create or update)
   */
  saveScorecard(scorecard: Scorecard): void {
    const scorecards = this.getAllScorecards();
    const index = scorecards.findIndex((s) => s.id === scorecard.id);

    if (index >= 0) {
      // Update existing
      scorecards[index] = scorecard;
    } else {
      // Create new
      scorecards.push(scorecard);
    }

    const serialized = scorecards.map((s) =>
      JSON.parse(this.serializeScorecard(s))
    );
    this.setToStorage(STORAGE_KEYS.SCORECARDS, JSON.stringify(serialized));
  }

  /**
   * Updates a scorecard (alias for saveScorecard for semantic clarity)
   */
  updateScorecard(scorecard: Scorecard): void {
    this.saveScorecard(scorecard);
  }

  /**
   * Deletes a scorecard by ID
   */
  deleteScorecard(id: string): void {
    const scorecards = this.getAllScorecards();
    const filtered = scorecards.filter((s) => s.id !== id);

    const serialized = filtered.map((s) =>
      JSON.parse(this.serializeScorecard(s))
    );
    this.setToStorage(STORAGE_KEYS.SCORECARDS, JSON.stringify(serialized));
  }

  /**
   * Gets a scorecard by evaluation ID, solution ID, and participant ID
   */
  getScorecardBySolution(evaluationId: string, solutionId: string, participantId?: string): Scorecard | null {
    const scorecards = this.getAllScorecards();

    // If participantId is provided, filter by it (new behavior)
    if (participantId) {
      return scorecards.find(
        (s) => s.evaluationId === evaluationId && s.solutionId === solutionId && s.participantId === participantId
      ) || null;
    }

    // Legacy behavior: return first match without participant filter (for backward compatibility)
    return scorecards.find(
      (s) => s.evaluationId === evaluationId && s.solutionId === solutionId
    ) || null;
  }

  /**
   * Saves scores for a specific solution in an evaluation
   * Creates or updates the scorecard automatically
   */
  saveScores(
    evaluationId: string,
    solutionId: string,
    scores: import('@/types/evaluation.types').CriteriaScore[],
    participantId: string,
    participantName: string
  ): void {
    const evaluation = this.getEvaluation(evaluationId);
    if (!evaluation) {
      console.error('Evaluation not found:', evaluationId);
      return;
    }

    const solution = evaluation.targetSolutions.find((s) => s.id === solutionId);
    if (!solution) {
      console.error('Solution not found:', solutionId);
      return;
    }

    // Find or create scorecard for this participant
    let scorecard = this.getScorecardBySolution(evaluationId, solutionId, participantId);

    if (!scorecard) {
      // Create new scorecard
      scorecard = {
        id: this.generateId(),
        evaluationId,
        participantId,
        solutionId,
        solutionName: solution.solutionName,
        participantName,
        scores: [],
        completionPercentage: 0,
        isComplete: false,
        isSubmitted: false,
        submittedDate: null,
        lastModified: new Date(),
      };
    }

    // Update scores
    scorecard.scores = scores;
    scorecard.lastModified = new Date();

    // Calculate completion percentage
    const allCriteria = evaluation.criteriaGroups.flatMap((g) => g.criteria);
    const scoredCriteria = scores.filter((s) => s.score !== null && s.score !== undefined);
    scorecard.completionPercentage = allCriteria.length > 0
      ? Math.round((scoredCriteria.length / allCriteria.length) * 100)
      : 0;
    scorecard.isComplete = scoredCriteria.length === allCriteria.length;

    // Save scorecard
    this.saveScorecard(scorecard);
  }

  /**
   * Submits a scorecard (locks it for editing)
   */
  submitScorecard(scorecardId: string): void {
    const scorecard = this.getScorecard(scorecardId);
    if (!scorecard) {
      console.error('Scorecard not found:', scorecardId);
      return;
    }

    scorecard.isSubmitted = true;
    scorecard.submittedDate = new Date();
    scorecard.lastModified = new Date();
    this.saveScorecard(scorecard);
  }

  /**
   * Reopens a submitted scorecard (unlocks it for editing)
   */
  reopenScorecard(scorecardId: string): void {
    const scorecard = this.getScorecard(scorecardId);
    if (!scorecard) {
      console.error('Scorecard not found:', scorecardId);
      return;
    }

    scorecard.isSubmitted = false;
    scorecard.submittedDate = null;
    scorecard.lastModified = new Date();
    this.saveScorecard(scorecard);
  }

  /**
   * Gets all scorecards for an evaluation (all participants, all solutions)
   */
  getAllScorecardsByEvaluation(evaluationId: string): Scorecard[] {
    const allScorecards = this.getAllScorecards();
    return allScorecards.filter((sc) => sc.evaluationId === evaluationId);
  }

  // ==========================================================================
  // IStorageUtilities Implementation
  // ==========================================================================

  /**
   * Generates a new UUID for entity IDs
   */
  generateId(): string {
    return createId();
  }

  /**
   * Checks and performs data migration if needed
   */
  checkDataVersion(): void {
    const storedVersion = this.getFromStorage<number>(STORAGE_KEYS.DATA_VERSION);

    if (storedVersion === null) {
      // First time setup
      this.setToStorage(STORAGE_KEYS.DATA_VERSION, DATA_VERSION.toString());
      return;
    }

    if (storedVersion < DATA_VERSION) {
      console.log(
        `Migrating data from version ${storedVersion} to ${DATA_VERSION}`
      );
      // Future: Add migration logic here
      this.setToStorage(STORAGE_KEYS.DATA_VERSION, DATA_VERSION.toString());
    }
  }
}

// ============================================================================
// Singleton Instance & Exports
// ============================================================================

/**
 * Singleton instance of the storage service
 */
export const storageService = new StorageService();

// Initialize data version check
storageService.checkDataVersion();

// ============================================================================
// Backward Compatibility Exports
// ============================================================================

/**
 * Generates a new UUID for entity IDs
 * @deprecated Use storageService.generateId() instead
 */
export const generateId = (): string => storageService.generateId();

/**
 * Gets all evaluations from storage
 * @deprecated Use storageService.getEvaluations() instead
 */
export const getEvaluations = (): Evaluation[] =>
  storageService.getEvaluations();

/**
 * Gets a single evaluation by ID
 * @deprecated Use storageService.getEvaluation() instead
 */
export const getEvaluation = (id: string): Evaluation | null =>
  storageService.getEvaluation(id);

/**
 * Saves an evaluation (create or update)
 * @deprecated Use storageService.saveEvaluation() instead
 */
export const saveEvaluation = (evaluation: Evaluation): void =>
  storageService.saveEvaluation(evaluation);

/**
 * Deletes an evaluation by ID
 * @deprecated Use storageService.deleteEvaluation() instead
 */
export const deleteEvaluation = (id: string): void =>
  storageService.deleteEvaluation(id);

/**
 * Gets the current evaluation template
 * @deprecated Use storageService.getTemplate() instead
 */
export const getTemplate = (): EvaluationTemplate | null =>
  storageService.getTemplate();

/**
 * Saves the evaluation template
 * @deprecated Use storageService.saveTemplate() instead
 */
export const saveTemplate = (template: EvaluationTemplate): void =>
  storageService.saveTemplate(template);

/**
 * Seeds a default template with sample criteria groups
 * @deprecated Use storageService.seedDefaultTemplate() instead
 */
export const seedDefaultTemplate = (): void =>
  storageService.seedDefaultTemplate();

/**
 * Gets all scorecards for a specific evaluation
 * @deprecated Use storageService.getScorecards() instead
 */
export const getScorecards = (evaluationId: string): Scorecard[] =>
  storageService.getScorecards(evaluationId);

/**
 * Gets a single scorecard by ID
 * @deprecated Use storageService.getScorecard() instead
 */
export const getScorecard = (scorecardId: string): Scorecard | null =>
  storageService.getScorecard(scorecardId);

/**
 * Saves a scorecard (create or update)
 * @deprecated Use storageService.saveScorecard() instead
 */
export const saveScorecard = (scorecard: Scorecard): void =>
  storageService.saveScorecard(scorecard);

/**
 * Updates a scorecard (alias for saveScorecard for semantic clarity)
 * @deprecated Use storageService.updateScorecard() instead
 */
export const updateScorecard = (scorecard: Scorecard): void =>
  storageService.updateScorecard(scorecard);

/**
 * Deletes a scorecard by ID
 * @deprecated Use storageService.deleteScorecard() instead
 */
export const deleteScorecard = (id: string): void =>
  storageService.deleteScorecard(id);

/**
 * Checks and performs data migration if needed
 * @deprecated Use storageService.checkDataVersion() instead
 */
export const checkDataVersion = (): void =>
  storageService.checkDataVersion();
